/**
 * KAN-837: поле формы профиля менеджера (подпись, подсказка, ошибка под полем).
 * KAN-845: общее для вкладки "Профиль" и шага "Телефон" экрана "Начало работы".
 */
import type { InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/** Подсказка под телефоном менеджера: и в профиле, и в шаге "Телефон" экрана "Начало работы". */
export const PHONE_HINT = 'На этот номер мы звоним, когда вы нажимаете "Позвонить" у клиента';

interface ProfileFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  /** Префикс id, если форма показана в двух местах одной страницы. */
  idPrefix?: string;
}

export function ProfileField({
  name,
  label,
  value,
  onChange,
  error,
  hint,
  idPrefix = 'profile',
  ...rest
}: ProfileFieldProps): JSX.Element {
  const id = `${idPrefix}-${name}`;
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-900">
        {label}
      </label>
      <input
        id={id}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        className={cn(
          'h-12 w-full rounded-xl border bg-white px-3 text-base outline-none focus:border-blue-500',
          error ? 'border-red-500' : 'border-gray-300'
        )}
        {...rest}
      />
      {hint && (
        <p id={`${id}-hint`} className="text-xs text-gray-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-sm text-red-600" data-testid={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
